import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  deleteLiveTrackingItem,
  fetchLiveTrackingItems,
  markLiveTrackingOpened,
  reorderLiveTrackingItems,
  updateLiveTrackingItem,
  upsertLiveTrackingItem,
} from '../../services/liveTrackingService';
import {
  deleteLiveTrackingItemFromRealm,
  persistLiveTrackingItemsToRealm,
  pruneLiveTrackingItemsNotIn,
  readLiveTrackingItemsFromRealm,
} from '../../realm/liveTrackingRealm';
import { isNetworkFailure } from '../../realm/outboundQueue';
import { liveTrackingSourceSignature, matchLiveTrackingItemForMessage, liveTrackingCommentPreview, normalizeLiveTrackingItem } from '../../utils/liveTrackingLocal';

const asList = (payload) => (Array.isArray(payload) ? payload : []).map(normalizeLiveTrackingItem);

const newPendingId = () => `pending-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const asPending = (item, outboundOp, outboundRequest) => ({
  ...item,
  syncStatus: 'pending',
  outboundOp,
  outboundRequest: outboundRequest || null,
});

const persistItem = (item) => {
  if (item) persistLiveTrackingItemsToRealm(item).catch(() => {});
};

const persistList = (items) => {
  persistLiveTrackingItemsToRealm(items).catch(() => {});
};

export const loadLiveTracking = createAsyncThunk(
  'liveTracking/load',
  async (_, { dispatch, rejectWithValue }) => {
    const local = asList(await readLiveTrackingItemsFromRealm() || []);
    if (local.length) dispatch(setLiveTrackingLayout(local));
    try {
      const remote = asList(await fetchLiveTrackingItems());
      persistList(remote);
      await pruneLiveTrackingItemsNotIn(remote.map((item) => item.id));
      const pending = local.filter((row) => {
        const id = String(row.id || '');
        return row.syncStatus === 'pending' || id.startsWith('pending-') || row.outboundOp;
      });
      const byId = new Map(remote.map((item) => [item.id, item]));
      pending.forEach((item) => {
        if (item?.id && !byId.has(item.id)) byId.set(item.id, item);
      });
      return Array.from(byId.values()).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    } catch (error) {
      if (local.length) return local;
      return rejectWithValue(error.response?.data?.error || 'Failed to load live tracking');
    }
  }
);

export const enableLiveTracking = createAsyncThunk(
  'liveTracking/enable',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const item = normalizeLiveTrackingItem(await upsertLiveTrackingItem(payload));
      persistItem(item);
      return item;
    } catch (error) {
      if (!isNetworkFailure(error)) {
        return rejectWithValue(error.response?.data?.error || 'Failed to enable live tracking');
      }
      const state = getState();
      const local = {
        id: newPendingId(),
        userId: state.auth?.user?.id,
        itemType: payload?.itemType || 'NOTE',
        taskId: payload?.taskId || null,
        sourceMessageId: payload?.sourceMessageId || null,
        sourceChannelId: payload?.sourceChannelId || null,
        title: payload?.title || 'Untitled',
        info: payload?.info || '',
        boardLane: payload?.boardLane || 'TEAM',
        assigneeUserId: payload?.assigneeUserId || null,
        assigneeName: payload?.assigneeName || null,
        seenSignature: payload?.seenSignature || '',
        sortOrder: (state.liveTracking?.items || []).length,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const pending = asPending(normalizeLiveTrackingItem(local), 'create', payload);
      persistItem(pending);
      return pending;
    }
  }
);

export const saveLiveTrackingItem = createAsyncThunk(
  'liveTracking/save',
  async ({ id, payload }, { getState, rejectWithValue }) => {
    try {
      const item = normalizeLiveTrackingItem(await updateLiveTrackingItem(id, payload));
      persistItem(item);
      return item;
    } catch (error) {
      const existing = (getState().liveTracking?.items || []).find((row) => row.id === id);
      if (existing && isNetworkFailure(error)) {
        const local = asPending({ ...existing, ...payload, id }, String(id).startsWith('pending-') ? 'create' : 'update', payload);
        persistItem(local);
        return local;
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to update live tracking');
    }
  }
);

export const removeLiveTracking = createAsyncThunk(
  'liveTracking/remove',
  async (id, { getState, rejectWithValue }) => {
    if (String(id).startsWith('pending-')) {
      await deleteLiveTrackingItemFromRealm(id);
      return id;
    }
    try {
      await deleteLiveTrackingItem(id);
      await deleteLiveTrackingItemFromRealm(id);
      return id;
    } catch (error) {
      const existing = (getState().liveTracking?.items || []).find((row) => row.id === id);
      if (existing && isNetworkFailure(error)) {
        persistItem(asPending(existing, 'delete'));
        return id;
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to remove live tracking');
    }
  }
);

export const ackLiveTrackingOpened = createAsyncThunk(
  'liveTracking/opened',
  async ({ id, seenSignature }, { getState, rejectWithValue }) => {
    try {
      const item = normalizeLiveTrackingItem(await markLiveTrackingOpened(id, seenSignature));
      persistItem(item);
      return item;
    } catch (error) {
      const existing = (getState().liveTracking?.items || []).find((row) => row.id === id);
      if (existing) {
        const local = asPending({
          ...existing,
          seenSignature: seenSignature || existing.seenSignature,
          lastOpenedAt: new Date().toISOString(),
        }, String(id).startsWith('pending-') ? 'create' : 'opened');
        persistItem(local);
        return local;
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to mark opened');
    }
  }
);

export const reorderLiveTracking = createAsyncThunk(
  'liveTracking/reorder',
  async ({ ids, lanes }, { getState, rejectWithValue }) => {
    try {
      const items = asList(await reorderLiveTrackingItems(ids, lanes));
      persistList(items);
      return items;
    } catch (error) {
      const existing = getState().liveTracking?.items || [];
      if (!isNetworkFailure(error)) {
        return rejectWithValue(error.response?.data?.error || 'Failed to reorder');
      }
      const order = new Map((ids || []).map((id, index) => [String(id), index]));
      const next = existing.map((item) => asPending({
        ...item,
        boardLane: lanes?.[item.id] || item.boardLane,
        sortOrder: order.has(String(item.id)) ? order.get(String(item.id)) : item.sortOrder,
      }, String(item.id).startsWith('pending-') ? (item.outboundOp || 'create') : 'reorder'));
      persistList(next);
      return next;
    }
  }
);

export const mergeLiveTrackingFromSync = createAsyncThunk(
  'liveTracking/mergeFromSync',
  async (incoming) => {
    const items = asList(incoming);
    persistList(items);
    return items;
  }
);

export const recomputeLiveTrackingAttention = createAsyncThunk(
  'liveTracking/recomputeAttention',
  async (_, { getState }) => {
    const state = getState();
    const items = state.liveTracking?.items || [];
    const tasks = state.tasks?.tasks || [];
    const messagesByChannel = state.chat?.messages || {};
    const allMessages = Object.values(messagesByChannel || {}).flat();
    const attention = {};
    items.forEach((item) => {
      if (item.itemType === 'NOTE') return;
      const task = item.taskId
        ? tasks.find((row) => String(row.id) === String(item.taskId))
        : null;
      const message = item.sourceMessageId
        ? allMessages.find((row) => String(row.id) === String(item.sourceMessageId))
        : null;
      const current = liveTrackingSourceSignature(item, task, message);
      if (current && item.seenSignature && current !== item.seenSignature) {
        attention[item.id] = true;
      }
    });
    return attention;
  }
);

export const ingestLiveTrackingMessage = createAsyncThunk(
  'liveTracking/ingestMessage',
  async (message, { getState }) => {
    const state = getState();
    const currentUserId = state.auth?.user?.id;
    if (!message?.id || (currentUserId && String(message.senderId) === String(currentUserId))) {
      return null;
    }
    const channels = [
      ...(state.channels?.channels || []),
      ...(state.channels?.directMessages || []),
    ];
    const item = matchLiveTrackingItemForMessage(state.liveTracking?.items || [], message, channels);
    if (!item) return null;
    return {
      item,
      preview: liveTrackingCommentPreview(message),
      actorName: message.senderName || 'Someone',
    };
  }
);

const pushToast = (state, toast) => {
  const dedupeKey = `${toast.kind}-${toast.itemId}-${toast.body || ''}`;
  if (state.toasts.some((row) => row.dedupeKey === dedupeKey && Date.now() - row.at < 4000)) return;
  state.toasts.unshift({
    id: `lt-toast-${toast.itemId}-${Date.now()}`,
    dedupeKey,
    at: Date.now(),
    kind: toast.kind,
    itemId: toast.itemId,
    itemType: toast.itemType,
    taskId: toast.taskId,
    sourceMessageId: toast.sourceMessageId,
    title: toast.title || 'Live Tracking',
    body: toast.body || 'Update',
    actorName: toast.actorName || '',
  });
  if (state.toasts.length > 12) state.toasts.length = 12;
};

const liveTrackingSlice = createSlice({
  name: 'liveTracking',
  initialState: {
    items: [],
    attentionIds: {},
    attentionHydrated: false,
    toasts: [],
    pendingOpenId: null,
    loading: false,
    error: null,
  },
  reducers: {
    setLiveTrackingLayout: (state, action) => {
      const next = Array.isArray(action.payload) ? action.payload : [];
      state.items = next.map(normalizeLiveTrackingItem);
    },
    ackLiveTrackingLocal: (state, action) => {
      const { id, seenSignature } = action.payload || {};
      if (!id) return;
      const index = state.items.findIndex((row) => row.id === id);
      if (index === -1) return;
      state.items[index] = {
        ...state.items[index],
        seenSignature: seenSignature || state.items[index].seenSignature,
        lastOpenedAt: new Date().toISOString(),
      };
      delete state.attentionIds[id];
      state.toasts = state.toasts.filter((toast) => toast.itemId !== id);
    },
    clearLiveTrackingAttention: (state, action) => {
      const id = action.payload;
      if (!id) return;
      delete state.attentionIds[id];
      state.toasts = state.toasts.filter((toast) => toast.itemId !== id);
    },
    dismissLiveTrackingToast: (state, action) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
    clearLiveTrackingToasts: (state) => {
      state.toasts = [];
    },
    setLiveTrackingPendingOpen: (state, action) => {
      state.pendingOpenId = action.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLiveTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadLiveTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.items = asList(action.payload);
      })
      .addCase(loadLiveTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(enableLiveTracking.fulfilled, (state, action) => {
        const item = action.payload;
        if (!item?.id) return;
        const index = state.items.findIndex((row) => row.id === item.id);
        if (index === -1) state.items.push(item);
        else state.items[index] = item;
      })
      .addCase(saveLiveTrackingItem.fulfilled, (state, action) => {
        const item = action.payload;
        if (!item?.id) return;
        const index = state.items.findIndex((row) => row.id === item.id);
        if (index === -1) state.items.push(item);
        else state.items[index] = item;
      })
      .addCase(removeLiveTracking.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
        delete state.attentionIds[action.payload];
        state.toasts = state.toasts.filter((toast) => toast.itemId !== action.payload);
      })
      .addCase(ackLiveTrackingOpened.fulfilled, (state, action) => {
        const item = action.payload;
        if (!item?.id) return;
        const index = state.items.findIndex((row) => row.id === item.id);
        if (index === -1) state.items.push(item);
        else state.items[index] = item;
        delete state.attentionIds[item.id];
        state.toasts = state.toasts.filter((toast) => toast.itemId !== item.id);
      })
      .addCase(reorderLiveTracking.fulfilled, (state, action) => {
        state.items = asList(action.payload);
      })
      .addCase(mergeLiveTrackingFromSync.fulfilled, (state, action) => {
        const incoming = asList(action.payload);
        if (!incoming.length) return;
        const byId = new Map(state.items.map((row) => [row.id, row]));
        incoming.forEach((item) => {
          if (item?.id) byId.set(item.id, { ...(byId.get(item.id) || {}), ...item });
        });
        state.items = Array.from(byId.values());
      })
      .addCase(recomputeLiveTrackingAttention.fulfilled, (state, action) => {
        const next = action.payload && typeof action.payload === 'object' ? action.payload : {};
        if (state.attentionHydrated) {
          Object.keys(next).forEach((id) => {
            if (state.attentionIds[id]) return;
            const item = state.items.find((row) => row.id === id);
            if (!item) return;
            pushToast(state, {
              kind: 'STATUS',
              itemId: item.id,
              itemType: item.itemType,
              taskId: item.taskId,
              sourceMessageId: item.sourceMessageId,
              title: item.title || 'Live Tracking',
              body: 'Status or details updated',
            });
          });
        }
        const merged = { ...state.attentionIds, ...next };
        Object.keys(merged).forEach((id) => {
          if (!state.items.some((row) => row.id === id)) delete merged[id];
        });
        state.attentionIds = merged;
        state.attentionHydrated = true;
      })
      .addCase(ingestLiveTrackingMessage.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload?.item?.id) return;
        const item = payload.item;
        state.attentionIds[item.id] = true;
        pushToast(state, {
          kind: 'COMMENT',
          itemId: item.id,
          itemType: item.itemType,
          taskId: item.taskId,
          sourceMessageId: item.sourceMessageId,
          title: item.title || 'Live Tracking',
          body: payload.preview || 'New comment',
          actorName: payload.actorName,
        });
      });
  },
});

export const {
  setLiveTrackingLayout,
  ackLiveTrackingLocal,
  clearLiveTrackingAttention,
  dismissLiveTrackingToast,
  clearLiveTrackingToasts,
  setLiveTrackingPendingOpen,
} = liveTrackingSlice.actions;
export default liveTrackingSlice.reducer;
